import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { publicSiteLangs, localizedPath, langFromPath, langPrefix } from "@/lib/translations";
import { resetPrerenderReady } from "@/lib/prerender";

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content || "");
};

/** Basis für Canonical/hreflang: Haupt-Domain aus den Einstellungen (SITE_URL), sonst aktuelle Adresse. */
let SITE_ORIGIN = "";
export const setSiteOrigin = (url) => { try { SITE_ORIGIN = url ? new URL(url).origin : ""; } catch { SITE_ORIGIN = ""; } };
const origin = () => SITE_ORIGIN || window.location.origin;

/** Parallel-Adresse (*.emergent.host) und Domain ohne www → Haupt-Domain, damit Google nur eine Adresse kennt. Vorschau und Haupt-Domain bleiben unberührt. */
export const redirectToSiteOrigin = () => {
  if (!SITE_ORIGIN || window.location.origin === SITE_ORIGIN) return false;
  const host = window.location.hostname, main = new URL(SITE_ORIGIN).hostname;
  if (!/\.emergent\.host$/.test(host) && main !== `www.${host}`) return false;
  window.location.replace(SITE_ORIGIN + window.location.pathname + window.location.search + window.location.hash);
  return true;
};

/** Canonical-Überschreibung einer Seite (z. B. Objektliste mit Insel-Filter → Inselseite); gilt nur für genau diese Adresse. */
let CANON = null;
const setRobots = (noindex) => {
  const el = document.head.querySelector('meta[name="robots"][data-seo="1"]');
  if (noindex && !el) { const m = document.createElement("meta"); m.name = "robots"; m.content = "noindex, follow"; m.dataset.seo = "1"; document.head.appendChild(m); }
  if (!noindex && el) el.remove();
};

export const applyAlternates = () => {
  const drop = () => { document.head.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]').forEach((el) => el.remove()); };
  if (document.head.querySelector('meta[name="prerender-status-code"]')) return drop(); // 404-Seite: kein Canonical/hreflang
  if (document.head.querySelector('meta[name="robots"][content^="noindex"]')) return drop(); // noindex-Seiten: kein Canonical/hreflang
  const here = window.location.pathname + window.location.search;
  const path = CANON && CANON.at === here ? CANON.path : window.location.pathname;
  let canon = document.head.querySelector('link[rel="canonical"]');
  if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
  canon.href = origin() + localizedPath(path, langFromPath());
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  const alts = publicSiteLangs.map((l) => [l, origin() + localizedPath(path, l)]);
  alts.push(["x-default", origin() + localizedPath(path, "de")]);
  alts.forEach(([l, href]) => { const el = document.createElement("link"); el.rel = "alternate"; el.hreflang = l; el.href = href; document.head.appendChild(el); });
};

export const useJsonLd = (objects) => {
  useEffect(() => {
    const els = objects.filter(Boolean).map((o) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.text = JSON.stringify(o);
      document.head.appendChild(el);
      return el;
    });
    return () => els.forEach((e) => e.remove());
  }, [JSON.stringify(objects)]); // eslint-disable-line react-hooks/exhaustive-deps
};

// BreadcrumbList nur, wenn jede Stufe einen Namen hat (Google: „name oder item.name müssen angegeben werden“)
export const breadcrumbLd = (items) => {
  const list = (items || []).filter(Boolean);
  if (!list.length || list.some((b) => !(typeof b.name === "string" && b.name.trim()))) return null;
  // item-Adressen in der Sprache der Seite (sonst zeigt /en/tenerife auf das nicht existierende /tenerife)
  const lang = langFromPath();
  return { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: list.map((b, i) => ({ "@type": "ListItem", position: i + 1, name: b.name.trim(), item: origin() + localizedPath(b.to, lang) })) };
};


export const RouteSeo = ({ lang }) => {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    resetPrerenderReady();
    // Doppeltes Sprachpräfix (/es/es/bewertungen – z. B. aus Links mit eingetipptem /es/…) → einmal entfernen
    const dup = pathname.match(/^\/(en|es|nl|pl|fr|it)(?=\/|$)/);
    if (dup && langPrefix(lang)) { navigate((pathname.slice(dup[0].length) || "/") + search + hash, { replace: true }); return; }
    // Insel-Slug passend zur Sprache erzwingen (/en/teneriffa → /en/tenerife), sonst doppelte Adressen
    const full = window.location.pathname;
    const want = localizedPath(full, lang);
    if (want !== full) { navigate(want.slice(langPrefix(lang).length) + search + hash, { replace: true }); return; }
    document.documentElement.lang = lang; applyAlternates();
  }, [pathname, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

/** canonical: anderer Pfad als Canonical (ohne Sprachpräfix, Insel-Slug wird je Sprache übersetzt) · noindex: Aktionsseiten (Angebote, Last Minute) aus dem Index halten */
export const useSeo = ({ title, description, image, lang, canonical, noindex }) => {
  useEffect(() => {
    if (!title) return;
    CANON = canonical ? { at: window.location.pathname + window.location.search, path: canonical } : null;
    setRobots(!!noindex);
    document.title = title;
    document.documentElement.lang = lang || "de";
    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", origin() + window.location.pathname + window.location.search);
    setMeta("property", "og:site_name", "privateHOLIDAY");
    setMeta("property", "og:locale", { de: "de_DE", en: "en_GB", es: "es_ES", nl: "nl_NL", pl: "pl_PL", fr: "fr_FR", it: "it_IT" }[lang] || "de_DE");
    setMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    if (image) { setMeta("property", "og:image", image); setMeta("name", "twitter:image", image); }
    applyAlternates();
    return () => { document.title = "privateHOLIDAY"; CANON = null; setRobots(false); };
  }, [title, description, image, lang, canonical, noindex]);
};
